import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { emailService } from "@/services/emailService";
import type { EmailTemplate } from "@/types/email";

interface TemplateSelectorProps {
	value?: string;
	onChange: (template: EmailTemplate | null) => void;
	language?: 'en' | 'fr' | 'nl';
}

const CATEGORY_LABELS: Record<string, { en: string; fr: string; nl: string }> = {
	job_sharing: { en: "Job Sharing", fr: "Partage d'emploi", nl: "Vacature delen" },
	outreach: { en: "Outreach", fr: "Prospection", nl: "Outreach" },
	follow_up: { en: "Follow Up", fr: "Suivi", nl: "Opvolging" },
	custom: { en: "Custom", fr: "Personnalisé", nl: "Aangepast" },
};

export const TemplateSelector = ({ value, onChange, language = 'en' }: TemplateSelectorProps) => {
	const { t, i18n } = useTranslation();
	const [templates, setTemplates] = useState<EmailTemplate[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		loadTemplates();
	}, []);

	const loadTemplates = async () => {
		try {
			const data = await emailService.getTemplates();
			setTemplates(data);
		} catch (_error) {
			// Silently fail - templates will be empty
		} finally {
			setIsLoading(false);
		}
	};

	const groupedTemplates = templates.reduce<Record<string, EmailTemplate[]>>((acc, template) => {
		const category = template.category || 'custom';
		if (!acc[category]) acc[category] = [];
		acc[category].push(template);
		return acc;
	}, {});

	const getCategoryLabel = (category: string) => {
		const labels = CATEGORY_LABELS[category];
		if (!labels) return category;
		const lang = i18n.language?.substring(0, 2) || 'en';
		return (labels as any)[lang] || labels.en;
	};

	const handleChange = (slug: string) => {
		if (slug === "__none__") {
			onChange(null);
			return;
		}
		const template = templates.find(t => t.slug === slug) || null;
		onChange(template);
	};

	return (
		<Select value={value || "__none__"} onValueChange={handleChange} disabled={isLoading}>
			<SelectTrigger className="w-full">
				<SelectValue placeholder={t("email.compose.selectTemplate", "Select a template...")} />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="__none__">
					{t("email.compose.noTemplate", "No template (custom email)")}
				</SelectItem>
				{Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
					<SelectGroup key={category}>
						<SelectLabel className="text-xs uppercase tracking-wider text-gray-500">
							{getCategoryLabel(category)}
						</SelectLabel>
						{categoryTemplates.map(template => (
							<SelectItem key={template.slug} value={template.slug}>
								<div className="flex flex-col">
									<span>{template.name}</span>
									{template.description && (
										<span className="text-xs text-gray-500">{template.description}</span>
									)}
								</div>
							</SelectItem>
						))}
					</SelectGroup>
				))}
			</SelectContent>
		</Select>
	);
};
